import { mount } from 'svelte';
import NotebookContainer from './components/NotebookContainer.svelte';
import { resolveAccount } from './storage/account';
import './styles/paper.css';

/**
 * App entry point.
 *
 * Step 7 (STEP_7_SPEC.md D5): first run is an empty notebook list. The container
 * owns store lifecycle — the list is its front door, and a notebook exists only
 * when the user creates one. The per-browser `device_id` mints lazily
 * (`getOrCreateDeviceId`), and the container's `openNotebook` catches a notebook
 * up over `/log` before anything else (§8.2).
 *
 * R4.9 §5 adds the one thing that happens BEFORE the mount: the gate. It is the
 * single choke point, because it is the only place that can decide what the app
 * is — the relay's mode is a server fact, the account may be cached locally, and
 * the answer decides which owner's store this browser opens. Everything after it
 * (the container, the stores it builds, the URL) is downstream of that answer and
 * never asks the question again.
 *
 * `boot` is a function rather than a top-level `await`: the bundled entry (Vite's
 * default `build.target`) has no top-level await, and the app must not depend on
 * one browser-class more than it has to.
 */
const app = document.getElementById('app');
if (!app) throw new Error('#app not found');
/** Narrowed once, so `boot` mounts into a target the checker knows is there. */
const target: HTMLElement = app;

async function boot(): Promise<void> {
  // R7: the gate hands over the acting owner and this browser's device id rather
  // than a whole `Identity` — there is no `notebook_id` until a notebook is
  // chosen, and choosing is the container's job.
  const { owner_id, device_id, auth } = await resolveAccount();
  mount(NotebookContainer, { target, props: { auth, owner_id, device_id } });
}

void boot();
